import React from 'react';
import Typography from '@material-ui/core/Typography';
import { Box, Container } from '@material-ui/core';
import cx from 'classnames';
import { BREAKPOINTS_VALUES } from '../../theme';
import useStyles from './styles';
import { LazyLoadImage } from '../LazyloadImage';

const TEXT = 'text';
const IMAGE = 'image';

//object with different styling for single, first and second image
const IMAGE_DATA = {
  SINGLE: {
    wrapperClass: 'images-text-block__image-wrap',
    srcset: [{ width: 350 }, { width: 584 }],
    sizes: `(max-width:${BREAKPOINTS_VALUES['lg']}px) 350px, 584px`,
  },
  FIRST: {
    wrapperClass: 'images-text-block__image-wrap',
    srcset: [{ width: 584 }, { width: 350 }],
    sizes: `(max-width:${BREAKPOINTS_VALUES['lg']}px) 584px, 350px`,
  },
  SECOND: {
    wrapperClass: 'images-text-block__second-image-wrap',
    srcset: [{ width: 420 }, { width: 343 }],
    sizes: `(max-width:${BREAKPOINTS_VALUES['lg']}px) 420px, 343px`,
  },
};

export const Text = ({ classes, heading, isImageAndTextBlock, text }) => {
  return (
    <Box className={classes.textWrapper}>
      <Typography
        variant="h2"
        component="h2"
        className={cx(
          classes.itemHeading,
          'images-text-block__item-heading',
          isImageAndTextBlock && classes.leftAlign,
        )}
      >
        {heading}
      </Typography>
      <Typography className={cx(classes.itemText, 'images-text-block__item-text')}>
        {text}
      </Typography>
    </Box>
  );
};

const getImageData = (isSingle: boolean, isFirst: boolean) => {
  if (isSingle) return IMAGE_DATA.SINGLE;
  else if (isFirst) return IMAGE_DATA.FIRST;
  else return IMAGE_DATA.SECOND;
};

export const Image = ({ classes, image, alt, isSingle = false, isFirst = false }) => {
  const imageData = getImageData(isSingle, isFirst);
  const className = isSingle
    ? classes.singleImageWrap
    : isFirst
    ? classes.firstImageWrap
    : classes.secondImageWrap;

  return (
    <div className={cx(className, imageData.wrapperClass)}>
      <LazyLoadImage
        path={image}
        alt={alt}
        draggable={false}
        srcset={imageData.srcset}
        sizes={imageData.sizes}
      />
    </div>
  );
};

const ImageTextArrayBlock = ({ data }) => {
  const background = data?.background;
  const classes = useStyles(background)();
  const itemsToFilter = data?.items;
  const isOdd = data?.isOdd;

  function* filter(array, condition, maxSize) {
    if (!maxSize || maxSize > array.length) {
      maxSize = array.length;
    }
    let count = 0;
    let i = 0;
    while (count < maxSize && i < array.length) {
      if (condition(array[i])) {
        yield array[i];
        count++;
      }
      i++;
    }
  }

  const texts = Array.from(filter(itemsToFilter, (i) => i.type === TEXT, 1));
  const images = Array.from(filter(itemsToFilter, (i) => i.type === IMAGE, 2));
  const imagesLength = images?.length;
  const items = imagesLength === 2 ? [images[0], ...texts, images[1]] : [...images, ...texts];

  const heading = typeof data?.heading === 'string' ? data?.heading : data?.heading?.en;

  if (!items || items.length === 0) return null;
  const isImageAndTextBlock = !!(
    items.find((item) => item.type === IMAGE) && items.find((item) => item.type === TEXT)
  );

  return (
    <Box
      className={cx(
        classes.wrapper,
        background === 'Dark' ? 'dark-block' : 'light-block',
        'images-text-block',
      )}
    >
      <Container className={classes.container}>
        <Typography
          variant="h2"
          component="h2"
          align="center"
          className={cx(classes.heading, 'images-text-block__heading')}
        >
          {heading}
        </Typography>
        <div className={classes.contentWrapper}>
          <div className={cx(classes.content, !isOdd && classes.odd)}>
            {items &&
              items.map((item, index) => {
                const type = item?.type;
                return (
                  <React.Fragment key={index}>
                    {type === IMAGE && (
                      <Image
                        classes={classes}
                        image={item?.image}
                        alt={item?.alt}
                        isSingle={imagesLength === 1}
                        isFirst={imagesLength === 2 && index === 0}
                      />
                    )}
                    {type === TEXT && (
                      <Text
                        classes={classes}
                        heading={heading}
                        text={item?.text}
                        isImageAndTextBlock={isImageAndTextBlock}
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </div>
        </div>
      </Container>
    </Box>
  );
};

export default ImageTextArrayBlock;
